import { useState } from 'react';
import { Step } from '../FormModalMultiStep';

interface UseStepsLogicProps {
  steps: Step[];
  stepErrors: Step[];
  onStepChange: () => void;
  stepsRenderKey?: string;
}

interface StepState {
  stepID: string;
  index: number;
}

export const useStepsLogic = ({
  steps,
  stepErrors,
  onStepChange,
}: UseStepsLogicProps) => {
  const [stepPointer, setStepPointer] = useState(0);
  const [stepsHistory, setStepsHistory] = useState<Array<StepState>>([
    {
      stepID: steps[0].name,
      index: 0,
    },
  ]);

  const updateStepPointer = (pointer: number) => {
    setStepPointer(pointer);
    onStepChange();
  };

  const resetSteps = () => {
    updateStepPointer(0);

    setStepsHistory([
      {
        stepID: steps[0].name,
        index: 0,
      },
    ]);
  };

  const goToStep = (id: number | string) => {
    const stepIndex = steps.findIndex((step, index) => {
      if (index === id) {
        updateStepPointer(index);
        return true;
      } else {
        return false;
      }
    });

    if (stepIndex > -1) {
      setStepsHistory([
        ...stepsHistory,
        {
          stepID: steps[stepIndex].name,
          index: stepIndex,
        },
      ]);
      updateStepPointer(stepIndex);
      return;
    }

    if (typeof id !== 'string') {
      return;
    }

    const stepError = stepErrors.find((step) => step.name === id);

    if (stepError) {
      setStepsHistory([
        ...stepsHistory,
        {
          stepID: stepError.name,
          index: Infinity,
        },
      ]);
      updateStepPointer(Infinity);
    }
  };

  const hasNextStep = Boolean(steps.at(stepPointer + 1));

  const nextStep = () => {
    const nextPointer = stepPointer + 1;

    const nextStep = steps[nextPointer];

    setStepsHistory([
      ...stepsHistory,
      {
        stepID: nextStep.name,
        index: nextPointer,
      },
    ]);

    updateStepPointer(nextPointer);
  };

  const previousStep = () => {
    setStepsHistory([...stepsHistory.slice(0, -1)]);

    const previousStep = stepsHistory[stepsHistory.length - 2];

    const previousStepPointer = previousStep.index;
    updateStepPointer(previousStepPointer);
  };

  const stepState = stepsHistory[stepsHistory.length - 1];
  const step = steps.find((step) => step.name === stepState.stepID);
  const stepError = stepErrors.find((step) => step.name === stepState.stepID);

  /**
   * Normal steps have precendence over step errors since step errors are
   * out of the normal flow of `next` step.
   */
  const currentStep = step ?? stepError;

  return {
    step: currentStep,
    nextStep,
    previousStep,
    goToStep,
    stepPointer,
    hasNextStep,
    resetSteps,
  };
};
